/**
 * Skeleton loading components for list views and content placeholders
 * Provides animated loading states that match the shape of actual content
 */

import { cn } from '@/lib/utils';

// ============================================================================
// BASE SKELETON
// ============================================================================

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-surface-subtle',
        className
      )}
    />
  );
}

// ============================================================================
// TEXT SKELETONS
// ============================================================================

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            // Last line is shorter for natural text appearance
            i === lines - 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

// ============================================================================
// CARD SKELETONS
// ============================================================================

export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface p-4 space-y-3',
        className
      )}
    >
      <Skeleton className="h-5 w-2/3" />
      <SkeletonText lines={2} />
      <div className="flex justify-between items-center pt-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
    </div>
  );
}

// ============================================================================
// PROJECT CARD SKELETON
// ============================================================================

export function SkeletonProjectCard({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface p-6 space-y-4',
        className
      )}
    >
      {/* Header with logo and title */}
      <div className="flex items-start gap-4">
        <Skeleton className="h-12 w-12 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      {/* Stats row */}
      <div className="flex gap-4">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-8 w-16" />
      </div>
      {/* Footer */}
      <div className="flex justify-between items-center pt-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-20 rounded-lg" />
      </div>
    </div>
  );
}

// ============================================================================
// CREATIVE CARD SKELETON
// ============================================================================

export function SkeletonCreativeCard({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface overflow-hidden',
        className
      )}
    >
      {/* Thumbnail */}
      <Skeleton className="aspect-video w-full rounded-none" />
      {/* Content */}
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex justify-between items-center pt-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TABLE ROW SKELETON
// ============================================================================

interface SkeletonTableRowProps {
  columns?: number;
  className?: string;
}

export function SkeletonTableRow({
  columns = 4,
  className,
}: SkeletonTableRowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 py-4 px-4 border-b border-border',
        className
      )}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === 0 ? 'w-1/4' : i === columns - 1 ? 'w-16' : 'w-1/6'
          )}
        />
      ))}
    </div>
  );
}

// ============================================================================
// LIST SKELETON
// ============================================================================

interface SkeletonListProps {
  count?: number;
  className?: string;
}

export function SkeletonList({ count = 5, className }: SkeletonListProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-border bg-surface">
          <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// GRID SKELETON
// ============================================================================

interface SkeletonGridProps {
  count?: number;
  columns?: 2 | 3 | 4;
  CardComponent?: React.ComponentType<SkeletonProps>;
  className?: string;
}

export function SkeletonGrid({
  count = 6,
  columns = 3,
  CardComponent = SkeletonCard,
  className,
}: SkeletonGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  return (
    <div className={cn('grid gap-4', gridCols[columns], className)}>
      {Array.from({ length: count }).map((_, i) => (
        <CardComponent key={i} />
      ))}
    </div>
  );
}

// ============================================================================
// ANALYSIS SKELETON
// ============================================================================

export function SkeletonAnalysis({ className }: SkeletonProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Decision Card */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="flex items-center gap-4 mb-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <SkeletonText lines={3} />
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-surface p-4">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="rounded-xl border border-border bg-surface p-4">
        <Skeleton className="h-5 w-24 mb-4" />
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonTableRow key={i} columns={3} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// STATS BAR SKELETON
// ============================================================================

export function SkeletonStatsBar({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'flex gap-4 p-4 rounded-xl border border-border bg-surface',
        className
      )}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex-1 text-center">
          <Skeleton className="h-8 w-12 mx-auto mb-1" />
          <Skeleton className="h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// MEMBER LIST SKELETON
// ============================================================================

export function SkeletonMemberList({ count = 3, className }: SkeletonListProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface"
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
